import React, { useEffect, useRef, useState } from "react";
import { OverlayView } from "@react-google-maps/api";
import { computeBearing, lerpPosition, haversineDistanceMeters } from "../utils/geo";
import "./MovingVehicleMarker.css";

interface Props {
  lat: number;
  lng: number;
  prevLat?: number;
  prevLng?: number;
  speed?: number;
  plate?: string;
  rotationOffset?: number; // degrees to add to computed heading
  rotation?: number;
  onPositionUpdate?: (pos:{lat:number,lng:number}) => void;
  instant?: boolean;
  playbackSpeed?: number;
  stepDurationMs?: number;
}

const MovingVehicleMarker: React.FC<Props> = ({ lat, lng, prevLat, prevLng, speed, plate, rotationOffset, rotation, onPositionUpdate, instant, playbackSpeed, stepDurationMs }) => {
  const [pos, setPos] = useState({ lat, lng });
  const headingRef = useRef(0);
  const headingHistoryRef = useRef<number[]>([]);
  const [currentHeading, setCurrentHeading] = useState<number>(0);
  const animRef = useRef<number | null>(null);

  const lastFrameTimeRef = useRef<number | null>(null);
  function shortestDelta(target: number, current: number) {
    let d = ((target - current + 540) % 360) - 180;
    return d;
  }

  function computeMaxDegPerSec(speedKph?: number) {
    const BASE_CAP = 90;
    if (!speedKph && speedKph !== 0) return BASE_CAP;
    const s = Math.max(0, speedKph || 0);
    if (s < 5) return Math.max(180, BASE_CAP);
    if (s < 30) return 180;
    return BASE_CAP;
  }

  useEffect(() => {
    const from = pos;
    const to = { lat, lng };
    const distance = haversineDistanceMeters(from, to);

    const rawFromLat = prevLat ?? from.lat;
    const rawFromLng = prevLng ?? from.lng;
    const rawToLat = lat; // backward-looking: compute heading from prev -> current
    const rawToLng = lng;
    // compute heading only for meaningful movement (> 0.5 meters) to avoid jitter
    if (distance >= 0.5 && rawFromLat !== undefined && rawFromLng !== undefined) {
      const h = computeBearing(rawFromLat, rawFromLng, rawToLat, rawToLng);
      const hist = headingHistoryRef.current;
      hist.push(h);
      if (hist.length > 6) hist.shift();
      headingHistoryRef.current = hist;
      const toRad = (d: number) => (d * Math.PI) / 180;
      const toDeg = (r: number) => (r * 180) / Math.PI;
      let sx = 0;
      let sy = 0;
      for (const a of hist) {
        sx += Math.cos(toRad(a));
        sy += Math.sin(toRad(a));
      }
      if (sx === 0 && sy === 0) {
        headingRef.current = h;
      } else {
        const mean = (toDeg(Math.atan2(sy, sx)) + 360) % 360;
        headingRef.current = mean;
      }
    }

    // If the jump is extremely small, skip animation to avoid micro-jitter
    if (distance < 0.5) {
      setPos(to);
      if (typeof onPositionUpdate === 'function') {
        try { onPositionUpdate(to); } catch (e) {}
      }
      return;
    }

    // If instant is requested, jump directly to the new position and heading
    if (instant) {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      setPos(to);
      setCurrentHeading(headingRef.current);
      if (typeof onPositionUpdate === 'function') {
        try { onPositionUpdate(to); } catch (e) {}
      }
      return;
    }

    // teleport clamp: very large jumps are teleports (device jump) — snap instead of long animation
    if (distance > 200) {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      setPos(to);
      setCurrentHeading(headingRef.current);
      if (typeof onPositionUpdate === 'function') {
        try { onPositionUpdate(to); } catch (e) {}
      }
      return;
    }

    let duration: number;
    if (typeof stepDurationMs === 'number' && stepDurationMs > 0) {
      duration = Math.max(20, Math.min(5000, Math.round(stepDurationMs)));
    } else {
      const baseDuration = 800;
      const speedFactor = speed ? Math.max(0.3, Math.min(2, 30 / (speed + 1))) : 1;
      const distanceFactor = Math.min(5, Math.max(1, distance / 10));
      const playback = Math.max(0.1, playbackSpeed || 1);
      const rawDuration = baseDuration * speedFactor * distanceFactor;
      duration = Math.max(50, Math.min(2500, rawDuration / playback));
    }

    const start = performance.now();
    const ease = (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    if (animRef.current) cancelAnimationFrame(animRef.current);
    function step(now: number) {
      const dtSec = lastFrameTimeRef.current ? Math.max(1e-3, (now - (lastFrameTimeRef.current || now)) / 1000) : (now - start) / 1000;
      lastFrameTimeRef.current = now;
      const rawT = Math.min(1, (now - start) / duration);
      const t = ease(rawT);
      setPos(lerpPosition(from, to, t));
      if (typeof onPositionUpdate === 'function') {
        try { onPositionUpdate(lerpPosition(from, to, t)); } catch (e) {}
      }
      const targetHeading = headingRef.current;
      setCurrentHeading((prev) => {
        const delta = shortestDelta(targetHeading, prev);
        const speedFactor = Math.min(3, (playbackSpeed || 1));
        const baseAlpha = 0.08;
        const alpha = Math.min(0.6, baseAlpha + 0.06 * speedFactor);
        let desiredDelta = delta * alpha;
        const maxDegPerSec = computeMaxDegPerSec(speed);
        const maxDeltaThisFrame = maxDegPerSec * dtSec;
        if (Math.abs(desiredDelta) > maxDeltaThisFrame) desiredDelta = Math.sign(desiredDelta) * maxDeltaThisFrame;
        const next = (prev + desiredDelta + 360) % 360;
        return next;
      });

      if (t < 1) {
        animRef.current = requestAnimationFrame(step);
      } else {
        animRef.current = null;
      }
    }
    animRef.current = requestAnimationFrame(step);

    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [lat, lng, instant, speed, prevLat, prevLng, onPositionUpdate]);

  const effectiveHeading = typeof rotation === 'number' && Number.isFinite(rotation) ? rotation : currentHeading;
  const rotationDeg = (effectiveHeading + (rotationOffset || 0)) % 360;
  const src = '/icons/car.png';
  return (
    <OverlayView position={{ lat: pos.lat, lng: pos.lng }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
      <div style={{ transform: `translate(-50%,-50%) rotate(${rotationDeg}deg)` }} className="vehicle-marker">
        <img className="vehicle-icon" src={src} alt="vehicle" />
        {plate ? <div className="plate-label">{plate}</div> : null}
      </div>
    </OverlayView>
  );
};

export default MovingVehicleMarker;
